import { isClanPreloaded } from '~/helpers/clan';
import { getLastRegularSeason } from '~/helpers/ladder';
import { getSeasonTeams } from '~/helpers/seasons';
import { clanItemSelector } from '~/store/selectors/currentAccountSelector';
import { createAppSelector } from '~/store/utils/createSelector';

import type { WowsLadderRatingTeam } from '~/Actions/ActionClanProfile';
import type { RootState } from '~/store';
import type { Nullable } from '~/types/generics';

const currentClanIdSelector = (state: RootState) => state.currentAccount?.clanId || null;

const clansItemsSelector = (state: RootState) => state.clans.items;

export const isClansFetchingSelector = (state: RootState) => state.clans.isFetching;

export const clanSelector = createAppSelector(
  [clansItemsSelector, (_: RootState, clanId: number) => clanId],
  (clansItems, clanId) => {
    return clansItems[clanId] || null;
  },
);

export const clanStatsSelector = (clanId: number) =>
  createAppSelector(clanItemSelector(clanId), (clan) => {
    return clan && !isClanPreloaded(clan) ? clan.stats : {};
  });

export const currentClanSelector = createAppSelector(
  [currentClanIdSelector, clansItemsSelector],
  (currentClanId, clanItems) => {
    return currentClanId ? clanItems[currentClanId] || null : null;
  },
);

export const clanLadderSelector = (state: RootState, clanId: number) => {
  return state.clans.wowsLadder[clanId];
};

export const clanHeaderLadderVMSelector = createAppSelector(
  [(state: RootState, clanId: number) => state.clans.wowsLadder[clanId]],
  (wowsLadder) => {
    const lastRegularSeason = getLastRegularSeason();
    const lastRegularSeasonId = lastRegularSeason?.id;

    if (!wowsLadder || !lastRegularSeasonId) {
      return {
        lastRegularSeasonData: undefined as WowsLadderRatingTeam | undefined,
        seasonTeams: null as Nullable<WowsLadderRatingTeam[]>,
        hideLeagueIcon: true,
      };
    }

    const lastRegularSeasonData = wowsLadder.ratings.find((item) => {
      return item.is_best_season_rating && item.season_number === lastRegularSeasonId;
    });

    let hideLeagueIcon = false;

    if (!lastRegularSeasonData) {
      hideLeagueIcon = true;
    } else if (
      lastRegularSeasonData.battles_count === 0 &&
      lastRegularSeasonData.public_rating <= wowsLadder.initial_public_rating
    ) {
      hideLeagueIcon = true;
    }

    const seasonTeams =
      wowsLadder.ratings.length > 0 && lastRegularSeason
        ? getSeasonTeams(wowsLadder.ratings, lastRegularSeason.id)
        : null;

    return {
      lastRegularSeasonData,
      seasonTeams,
      hideLeagueIcon,
    };
  },
);
